import {
  Alert, Box, Button, IconButton, LinearProgress, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { FC, ReactNode } from 'react';

import { Computing } from '../../interfaces/models';
import { formatValue } from '../../utils/format';
import { useGargi } from '../AskGargi/context';

export const pct = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined || !Number.isFinite(value) ? '—' : `${(value * 100).toFixed(digits)}%`;

export const count = (value: number | null | undefined) => formatValue(value, 'count');

// Category values arrive lower-cased; show them the way people write them.
export const titleCase = (value: string) => value.replace(/\b([a-z])/g, (c) => c.toUpperCase());

export const humanize = (field: string) => {
  const text = field.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

interface PanelProps {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
  note?: ReactNode;
  children: ReactNode;
  // What to ask Gargi about this panel; by default its title and subtitle. false hides the button.
  ask?: string | false;
}

export const Panel: FC<PanelProps> = ({ title, subtitle, action, note, children, ask }) => {
  const gargi = useGargi();
  const question = ask === false ? null
    : ask ?? `Explain "${title}" for us${typeof subtitle === 'string' ? ` (${subtitle})` : ''}: what stands out, why, and what should we do about it?`;
  return (
  <Paper variant='outlined' sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, minWidth: 0 }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent='space-between' alignItems={{ sm: 'flex-start' }} sx={{ mb: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant='subtitle1' fontWeight={700}>{title}</Typography>
        {subtitle && <Typography variant='body2' color='text.secondary' sx={{ mt: 0.25 }}>{subtitle}</Typography>}
      </Box>
      <Stack direction='row' spacing={0.5} alignItems='center' sx={{ flexShrink: 0 }}>
        {action}
        {question && gargi.available && (
          <Tooltip title='Ask Gargi about this'>
            <IconButton size='small' color='primary' onClick={() => gargi.open(question, { panel: title })} aria-label={`Ask Gargi about ${title}`}>
              <AutoAwesomeRoundedIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Stack>
    {children}
    {note && (
      <Stack direction='row' spacing={0.75} alignItems='flex-start' sx={{ mt: 1.5 }}>
        <InfoOutlinedIcon sx={{ fontSize: 14, mt: '2px', color: 'text.secondary' }} />
        <Typography variant='caption' color='text.secondary'>{note}</Typography>
      </Stack>
    )}
  </Paper>
  );
};

export const Metric: FC<{ label: string; value: string; hint?: ReactNode; strong?: boolean }> = ({ label, value, hint, strong }) => (
  <Paper variant='outlined' sx={{ p: 2, borderRadius: 3, ...(strong ? { borderColor: 'primary.main', bgcolor: 'primary.light' } : {}) }}>
    <Typography variant='caption' color='text.secondary' fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
      {label}
    </Typography>
    <Typography variant='h5' fontWeight={700} sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    {hint && <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>{hint}</Typography>}
  </Paper>
);

export const MetricGrid: FC<{ children: ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
    {children}
  </Box>
);

export const TwoColumns: FC<{ children: ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' } }}>
    {children}
  </Box>
);

export interface Column<T> {
  key: string;
  label: string;
  align?: 'left' | 'right';
  render: (row: T) => ReactNode;
  width?: number | string;
}

// A compact table whose cells can hold chips and buttons (InsightTable only formats values).
export function RichTable<T>({ columns, rows, minWidth = 560, onRowClick }: { columns: Column<T>[]; rows: T[]; minWidth?: number; onRowClick?: (row: T) => void }) {
  return (
    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
      <Table size='small' sx={{ minWidth }}>
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.key} align={c.align ?? 'left'} width={c.width}
                sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} hover onClick={onRowClick ? () => onRowClick(row) : undefined} sx={onRowClick ? { cursor: 'pointer' } : undefined}>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align ?? 'left'} sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', verticalAlign: 'top' }}>
                  {c.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface ModuleGateProps {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  computing?: Computing;
  what: string;
  onRetry: () => void;
  children: ReactNode;
}

// Loading, computing (the first run of the day works in the background) and error states for a tab.
export const ModuleGate: FC<ModuleGateProps> = ({ isLoading, isError, error, computing, what, onRetry, children }) => {
  if (isError) {
    const forbidden = (error as any)?.response?.status === 403;
    return (
      <Alert severity='error' action={!forbidden && <Button color='inherit' size='small' onClick={onRetry}>Try again</Button>}>
        {forbidden ? 'This is available to CASA staff during the preview.' : `${what} could not be loaded. The analytics service may be restarting.`}
      </Alert>
    );
  }
  if (computing) {
    return (
      <Paper variant='outlined' sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant='subtitle1' fontWeight={700}>Preparing {what.toLowerCase()}…</Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5, mb: 2, maxWidth: 640 }}>
          Gargi works this out from the business&apos;s own bills and campaigns the first time it is opened, then keeps it
          (suggestions for a week, numbers for the day). The first run usually takes one to four minutes; after that it opens instantly. Started {computing.startedSecondsAgo}s ago.
        </Typography>
        <LinearProgress sx={{ borderRadius: 1 }} />
      </Paper>
    );
  }
  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant='rounded' height={96} sx={{ borderRadius: 3 }} />
        <Skeleton variant='rounded' height={260} sx={{ borderRadius: 3 }} />
      </Stack>
    );
  }
  return <>{children}</>;
};
